import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const DEFAULTS = {
  site_title: "Christoreel | African Christian Movies",
  meta_description: "Christoreel — the streaming home for African Christian cinema.",
  og_title: "Christoreel | African Christian Movies",
  og_description: "Stream curated African Christian movies. New titles weekly. Four quality tiers from ₦2,000/mo.",
  og_image_url: "https://christoreel.com/og-default.jpg",
};

const BrandingContext = createContext({ ...DEFAULTS, loading: true, refresh: () => {} });

// Set or create a <meta> tag in <head> with the given attribute+name and content.
function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function BrandingProvider({ children }) {
  const [state, setState] = useState({ ...DEFAULTS, loading: true });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/branding");
      setState({
        site_title: data.site_title || DEFAULTS.site_title,
        meta_description: data.meta_description || DEFAULTS.meta_description,
        og_title: data.og_title || DEFAULTS.og_title,
        og_description: data.og_description || DEFAULTS.og_description,
        og_image_url: data.og_image_url || DEFAULTS.og_image_url,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Mirror admin-set branding into the live document so every visitor (and
  // any scraper that runs JS, e.g. LinkedIn) sees the up-to-date metadata.
  useEffect(() => {
    if (state.loading) return;
    if (state.site_title) document.title = state.site_title;
    setMeta("name", "description", state.meta_description);
    setMeta("property", "og:title", state.og_title);
    setMeta("property", "og:description", state.og_description);
    setMeta("property", "og:image", state.og_image_url);
    setMeta("name", "twitter:title", state.og_title);
    setMeta("name", "twitter:description", state.og_description);
    setMeta("name", "twitter:image", state.og_image_url);
  }, [state]);

  return (
    <BrandingContext.Provider value={{ ...state, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
